import React, {useEffect, useRef} from "react";
import styles from "./ChatHistory.module.css";
import {
    ChatHistoryDTO,
    EmailSendingLogUi,
    MessageFeedbackUi,
    MessageRole,
    SurveyResponseUi
} from "../../../../../api/generated";
import {useAppDispatch, useAppSelector} from "../../../../../app/hooks";
import {widgetAppearanceSettingsSelector} from "../../transcriptSlice";
import {UserMessage} from "./components/userMessage/UserMessage";
import {AssistantMessage} from "./components/assistantMessage/AssistantMessage";
import {ToolMessage} from "./components/toolMessage/ToolMessage";
import {ConversationSeparator} from "./components/conversationSeparator/ConversationSeparator";
import {DateSeparator} from "./components/dateSeparator/DateSeparator";
import {AgentTransitionSeparator} from "./components/agentTransitionSeparator/AgentTransitionSeparator";
import {showCopiedSuccessfullyToastNotification} from "../../../../elements/toastNotification/toastNotificationSlice";
import {HierarchyContextTransition} from "./components/hierarchyContextTransition/HierarchyContextTransition";
import {getHierarchyContextKey} from "./components/hierarchyContextTransition/hierarchyContextUtils";
import {EmailSendingLogTransition} from "./components/emailSendingLogTransition/EmailSendingLogTransition";
import {MessageFeedbackNote} from "./components/messageFeedback/MessageFeedbackNote";
import {SurveyResultCard} from "./components/surveyResult/SurveyResultCard";

interface FocusHighlightProps {
    isFocused: boolean
    focusedRef: React.RefObject<HTMLDivElement>
    children: React.ReactNode
}

const FocusHighlight: React.FC<FocusHighlightProps> = ({isFocused, focusedRef, children}) => {
    if (!isFocused) {
        return <>{children}</>
    }
    return <div ref={focusedRef} className={styles.focused} data-testid="focused-feedback">
        {children}
    </div>
}

interface ChatHistoryProps {
    chatHistoryList: ChatHistoryDTO[]
    emailSendingLogs?: EmailSendingLogUi[]
    messageFeedback?: MessageFeedbackUi[]
    surveys?: SurveyResponseUi[]
    // Opened from the Feedback page: scroll to and highlight the rated message or the survey
    focusedMessageId?: number
    focusedSurveyId?: number
}

export const ChatHistory: React.FC<ChatHistoryProps> = ({
                                                            chatHistoryList,
                                                            emailSendingLogs = [],
                                                            messageFeedback = [],
                                                            surveys = [],
                                                            focusedMessageId,
                                                            focusedSurveyId
                                                        }) => {

    const dispatch = useAppDispatch()
    const widgetAppearanceSettings = useAppSelector(widgetAppearanceSettingsSelector)

    const scrollRef = useRef<HTMLDivElement>(null);
    const lastMessageRef = useRef<HTMLDivElement>(null);
    const focusedRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        const lastMessage = lastMessageRef.current;
        const focusedElement = focusedRef.current;

        if (focusedElement) {
            requestAnimationFrame(() => {
                setTimeout(() => focusedElement.scrollIntoView({behavior: "smooth", block: "center"}), 0);
            });
            return;
        }

        if (!scrollContainer || !lastMessage) return;

        // Wait for layout to settle
        const handleScroll = () => {
            const containerHeight = scrollContainer.clientHeight;
            const messageHeight = lastMessage.offsetHeight;

            if (messageHeight <= containerHeight) {
                scrollContainer.scrollTo({
                    top: scrollContainer.scrollHeight,
                    behavior: "smooth"
                })
            } else {
                lastMessage.scrollIntoView({behavior: "smooth", block: "start"});
            }
        };

        requestAnimationFrame(() => {
            setTimeout(handleScroll, 0);
        });
    }, []);

    if (!widgetAppearanceSettings) {
        return <></>
    }

    const getWrapperStyles = (chatHistory: ChatHistoryDTO) => {
        if (chatHistory.messageRole === MessageRole.User) {
            return {display: "flex", justifyContent: "start"}
        } else if (chatHistory.messageRole === MessageRole.Assistant && chatHistory.message) {
            return {display: "flex", justifyContent: "end"}
        } else if (chatHistory.messageRole === MessageRole.Assistant || chatHistory.toolCalls?.length) {
            return {display: "flex", justifyContent: "center"}
        } else {
            console.error(`Unsupported messageRole: ${chatHistory.messageRole}`)
            return {display: "flex", justifyContent: "center"}
        }
    }

    const chatHistoryWithoutToolMessages = chatHistoryList
        .filter(ch => ([MessageRole.User, MessageRole.Assistant] as MessageRole[]).includes(ch.messageRole as MessageRole))

    const sortEmailLogs = (logs: EmailSendingLogUi[]) => [...logs].sort((a, b) => {
        const sentAtComparison = (a.sentAt || "").localeCompare(b.sentAt || "");
        return sentAtComparison !== 0 ? sentAtComparison : (a.id || 0) - (b.id || 0);
    });

    const visibleMessageIds = chatHistoryWithoutToolMessages.map(chatHistory => chatHistory.id);
    const visibleMessageIdSet = new Set(visibleMessageIds);
    const beforeFirstEmailLogs: EmailSendingLogUi[] = [];
    const emailLogsByAnchorId = new Map<number, EmailSendingLogUi[]>();

    sortEmailLogs(emailSendingLogs).forEach(log => {
        const lastMessageId = log.lastMessageId;
        const previousVisibleMessageIds = visibleMessageIds.filter(id => lastMessageId && id <= lastMessageId);
        const anchorId = lastMessageId && visibleMessageIdSet.has(lastMessageId)
            ? lastMessageId
            : previousVisibleMessageIds[previousVisibleMessageIds.length - 1];

        if (!anchorId) {
            beforeFirstEmailLogs.push(log);
            return;
        }

        emailLogsByAnchorId.set(anchorId, [...(emailLogsByAnchorId.get(anchorId) || []), log]);
    });

    const feedbackByMessageId = new Map(messageFeedback.map(feedback => [feedback.chatHistoryId, feedback]));
    const surveysByAnchorIndex = new Map<number, SurveyResponseUi[]>();
    surveys.forEach(survey => {
        const surveyTime = new Date(survey.createdAt).getTime();
        let anchorIndex: number | undefined;
        let lastIndexInConversation: number | undefined;
        chatHistoryWithoutToolMessages.forEach((msg, i) => {
            if (msg.conversation.id !== survey.conversationId) {
                return;
            }
            lastIndexInConversation = i;
            if (new Date(msg.timestamp).getTime() <= surveyTime) {
                anchorIndex = i;
            }
        });
        const index = anchorIndex ?? lastIndexInConversation;
        if (index !== undefined) {
            surveysByAnchorIndex.set(index, [...(surveysByAnchorIndex.get(index) || []), survey]);
        }
    });

    const agentTransitions = new Map<number, string>();
    const lastAgentByConversation = new Map<number, string>();
    chatHistoryWithoutToolMessages.forEach((msg, i) => {
        if (msg.messageRole !== MessageRole.Assistant || !msg.responseAgent) return;
        const convId = msg.conversation.id;
        if (lastAgentByConversation.get(convId) !== msg.responseAgent) {
            agentTransitions.set(i, `Switched to ${msg.responseAgent} Agent`);
        }
        lastAgentByConversation.set(convId, msg.responseAgent);
    });

    const contextTransitions = new Map<number, ChatHistoryDTO>();
    const lastContextByConversation = new Map<number, string>();
    chatHistoryWithoutToolMessages.forEach((msg, i) => {
        const convId = msg.conversation.id;
        const contextKey = getHierarchyContextKey(msg);
        const previousContextKey = lastContextByConversation.get(convId);

        if (previousContextKey !== undefined && previousContextKey !== contextKey) {
            contextTransitions.set(i, msg);
        }

        lastContextByConversation.set(convId, contextKey);
    });

    const copyChatIdAndShowToast = () => {
        const user = chatHistoryList[0].user;
        if (user && user.chatId) {
            navigator.clipboard.writeText(user.chatId);
            dispatch(
                showCopiedSuccessfullyToastNotification({fieldName: "Chat Id"})
            );
        }
    }

    return <div className={styles.wrapper}>
        <div
            className={styles.title}
            onClick={copyChatIdAndShowToast}
        >
            Chat Summary
        </div>
        <div ref={scrollRef} className={styles.content}>
            {beforeFirstEmailLogs.map(log => (
                <EmailSendingLogTransition
                    key={`email-log-${log.id}`}
                    log={log}
                    routingDecision={log.routingDecision}
                />
            ))}
            {chatHistoryWithoutToolMessages
                .map((chatHistory, index) => {
                    const isLastMessage = index === chatHistoryWithoutToolMessages.length - 1;
                    const messageRole = chatHistory.messageRole
                    const wrapperStyles = getWrapperStyles(chatHistory)

                    const prev = index > 0 ? chatHistoryWithoutToolMessages[index - 1] : null;
                    const prevConversationId = prev?.conversation.id ?? null;
                    const newConversationId = chatHistory.conversation.id
                    const isNewConversation = newConversationId !== prevConversationId;
                    const transitionAgent = agentTransitions.get(index);
                    const transitionContext = contextTransitions.get(index);

                    const prevDate = prev ? new Date(prev.timestamp).toDateString() : null;
                    const currentDate = new Date(chatHistory.timestamp).toDateString();
                    const isNewDate = currentDate !== prevDate;

                    const messageElement = (() => {
                        if (messageRole === MessageRole.User) {
                            return <UserMessage chatHistory={chatHistory}/>
                        } else if (messageRole === MessageRole.Assistant && chatHistory.message) {
                            return <AssistantMessage
                                chatHistory={chatHistory}
                                widgetAppearanceSettings={widgetAppearanceSettings}
                            />
                        } else if (messageRole === MessageRole.Assistant && !!chatHistory.toolCalls?.length) {
                            return <ToolMessage
                                chatHistoryId={chatHistory.id}
                                chatHistoryList={chatHistoryList}
                            />
                        } else {
                            console.error(`Unsupported messageRole: ${messageRole}`)
                            return <></>
                        }
                    })()

                    return <React.Fragment key={chatHistory.id}>
                        {isNewDate && (
                            <DateSeparator timestamp={chatHistory.timestamp}/>
                        )}
                        {isNewConversation && index > 0 && (
                            <ConversationSeparator conversationId={newConversationId}/>
                        )}
                        {transitionAgent && (
                            <AgentTransitionSeparator agentName={transitionAgent}/>
                        )}
                        {transitionContext && (
                            <HierarchyContextTransition chatHistory={transitionContext}/>
                        )}
                        <FocusHighlight isFocused={chatHistory.id === focusedMessageId} focusedRef={focusedRef}>
                            <div
                                ref={isLastMessage ? lastMessageRef : null}
                                style={wrapperStyles}
                            >
                                {messageElement}
                            </div>
                            {messageRole === MessageRole.Assistant && feedbackByMessageId.has(chatHistory.id) && (
                                <MessageFeedbackNote feedback={feedbackByMessageId.get(chatHistory.id)!}/>
                            )}
                        </FocusHighlight>
                        {surveysByAnchorIndex.get(index)?.map(survey => (
                            <FocusHighlight key={`survey-${survey.id}`} isFocused={survey.id === focusedSurveyId}
                                            focusedRef={focusedRef}>
                                <SurveyResultCard survey={survey}/>
                            </FocusHighlight>
                        ))}
                        {emailLogsByAnchorId.get(chatHistory.id)?.map(log => (
                            <EmailSendingLogTransition
                                key={`email-log-${log.id}`}
                                log={log}
                                routingDecision={log.routingDecision}
                            />
                        ))}
                    </React.Fragment>
                })}
        </div>
    </div>
}
